import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getToken } from 'selectors/auth'
import env from 'config/env'
import { logout } from 'actions/auth'
import GraphQLError from 'lib/graphql-error'

/**
 * Axios client configured with interceptors
 * - Automatically adds Authorization header with JWT token
 * - Handles 401 errors by redirecting to login
 */

const apiClient: AxiosInstance = axios.create({
  baseURL: env.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Request interceptor
 * Add Authorization header with token to every request
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken()

    if (token && config.headers) {
      config.headers.Authorization = token
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

/**
 * Response interceptor
 * - Check for GraphQL errors in response body (status 200 with errors array)
 * - Handle 401 errors by redirecting to login page
 */
apiClient.interceptors.response.use(
  (response) => {
    const errors = response.data?.errors
    if (errors?.length) {
      return Promise.reject(new GraphQLError(errors))
    }
    return response
  },
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      logout()
    }
    const gqlErrors = error.response?.data?.errors
    if (gqlErrors?.length) {
      return Promise.reject(new GraphQLError(gqlErrors))
    }
    return Promise.reject(error)
  },
)

export default apiClient
